/**
 * UserMenu — Dropdown con avatar, ajustes, guía PDF y logout.
 * Extraído de DashboardLayout.jsx (iter.246 · Fase 8 UX Refactor).
 *
 * Props:
 *  - user: objeto usuario ({name, email, picture}).
 *  - currentRole: rol actual (para mostrar/ocultar link a Config).
 *  - collapsed: si el sidebar está colapsado.
 *  - userGuideVisible: bool, si mostrar link a guía PDF.
 *  - onLogout: callback al pulsar Logout.
 */
import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import { ChevronDown, Settings, BookOpen, LogOut, RotateCcw, Home, Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
  DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent,
  DropdownMenuRadioGroup, DropdownMenuRadioItem,
} from "../ui/dropdown-menu";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";
import { Button } from "../ui/button";
import { toast } from "sonner";
import api from "../../lib/api";
import { getInitials } from "../../lib/utils";

const LANDING_OPTIONS = [
  { value: "dashboard", label: "Panel de control" },
  { value: "sales",     label: "Ventas de hoy"    },
  { value: "inventory", label: "Inventario"       },
  { value: "menus",     label: "Mis menús"        },
  { value: "more",      label: "Configuración"    },
];

export function UserMenu({ user, currentRole, collapsed, userGuideVisible, onLogout }) {
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [landing, setLanding] = useState("dashboard");
  const [savingLanding, setSavingLanding] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.request("/auth/me");
        if (!cancelled) setLanding(me?.preferences?.default_landing || "dashboard");
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!user) return null;

  const handleLandingChange = async (value) => {
    if (value === landing || savingLanding) return;
    setSavingLanding(true);
    const prev = landing;
    setLanding(value); // optimistic
    try {
      await api.request("/auth/me/preferences", {
        method: "PATCH",
        body: JSON.stringify({ default_landing: value }),
      });
      const nice = LANDING_OPTIONS.find(o => o.value === value)?.label || value;
      toast.success(`Pantalla de inicio: ${nice}`);
    } catch (e) {
      setLanding(prev);
      toast.error(`No se pudo guardar: ${e?.message || "error"}`);
    } finally {
      setSavingLanding(false);
    }
  };

  const handleReset = async () => {
    setResetting(true);
    try {
      await api.request("/auth/me/preferences/reset", { method: "POST" });
      // Limpiar overrides locales para que al recargar se lean los defaults del server.
      try {
        localStorage.removeItem("mg_sidebar_collapsed");
        localStorage.removeItem("menugenius-theme");
        // AdvancedBlock toggle state keys (mg_advblock_*)
        Object.keys(localStorage).forEach((k) => {
          if (k.startsWith("mg_advblock_")) localStorage.removeItem(k);
        });
      } catch { /* storage blocked */ }
      toast.success("Preferencias restablecidas. Recargando…");
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error(`No se pudieron restablecer: ${e?.message || "error"}`);
    } finally {
      setResetting(false);
      setResetOpen(false);
    }
  };

  return (
    <div className={`border-t border-border ${collapsed ? "p-2" : "p-4"}`}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            className={`w-full gap-3 ${collapsed ? "justify-center px-0" : "justify-start px-2"}`}
            data-testid="user-menu-trigger"
          >
            <Avatar className="w-8 h-8 flex-shrink-0">
              <AvatarImage src={user.picture} />
              <AvatarFallback className="bg-primary/10 text-primary text-sm">
                {getInitials(user.name)}
              </AvatarFallback>
            </Avatar>
            {!collapsed && (
              <>
                <div className="flex-1 text-left truncate">
                  <p className="text-sm font-medium truncate">{user.name}</p>
                  <p className="text-xs text-muted-foreground truncate">{user.email}</p>
                </div>
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              </>
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56">
          {currentRole === "owner" && (
            <DropdownMenuItem asChild>
              <Link to="/app/settings" className="cursor-pointer">
                <Settings className="w-4 h-4 mr-2" />
                Configuración
              </Link>
            </DropdownMenuItem>
          )}
          {userGuideVisible && (
            <DropdownMenuItem asChild data-testid="user-guide-dropdown-link">
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/user-guide.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                className="cursor-pointer"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Guía de usuario (PDF)
              </a>
            </DropdownMenuItem>
          )}
          <DropdownMenuSub>
            <DropdownMenuSubTrigger data-testid="landing-picker-trigger">
              <Home className="w-4 h-4 mr-2" />
              Pantalla de inicio
              {landing !== "dashboard" && (
                <span
                  data-testid="landing-custom-badge"
                  title="Tu pantalla de inicio está personalizada"
                  className="ml-2 inline-flex items-center gap-0.5 rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0 h-4 font-medium"
                >
                  <Sparkles className="w-2.5 h-2.5" />
                  Personalizado
                </span>
              )}
            </DropdownMenuSubTrigger>
            <DropdownMenuSubContent>
              <DropdownMenuRadioGroup value={landing} onValueChange={handleLandingChange}>
                {LANDING_OPTIONS.map((opt) => (
                  <DropdownMenuRadioItem
                    key={opt.value}
                    value={opt.value}
                    disabled={savingLanding}
                    data-testid={`landing-option-${opt.value}`}
                  >
                    {opt.label}
                  </DropdownMenuRadioItem>
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuSub>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={(e) => { e.preventDefault(); setResetOpen(true); }}
            className="cursor-pointer"
            data-testid="reset-prefs-btn"
          >
            <RotateCcw className="w-4 h-4 mr-2" />
            Restablecer preferencias
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onLogout}
            className="text-destructive cursor-pointer"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Cerrar sesión
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={resetOpen} onOpenChange={setResetOpen}>
        <AlertDialogContent data-testid="reset-prefs-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Restablecer preferencias</AlertDialogTitle>
            <AlertDialogDescription>
              Esto restablecerá tu modo (Simple/Avanzado), tema (claro/oscuro), estado del menú lateral
              y el orden de módulos a los valores por defecto. Tu cuenta y datos del restaurante no se tocan.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReset}
              disabled={resetting}
              data-testid="reset-prefs-confirm"
            >
              {resetting ? "Restableciendo…" : "Sí, restablecer"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
